import React, { useEffect, useState, useRef } from 'react'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import cn from 'classnames'
import { get, sumBy } from 'lodash'
import queryString from 'query-string'

import GratuityHeader from '../components/GratuityHeader'
import Button from '../components/Button'

import { makeGetOrder } from '../selectors/order'

import { removeOrder, updateCardReaderData } from '../actions/order'

import styles from './SignatureError.module.scss'
import CardReader from '../components/CardReader'
import PeripheralBridge from '../utils/peripheralBridge'
import { clearCart } from '../actions/cart'
import { getPaymentType } from '../selectors/peripheral'
import { getDeviceSerial } from '../utils/formatters'
import { clearPromotionsOnOrder } from '../actions/promotion'
import { cardReaderApproved } from '../utils/orderUtils'

const CancelOrder = ({ orderId, orderNumber, requestId, transId, token, paymentType, onDone, onSuccess }) => {
  const [viewState, setViewState] = useState('loading')

  useEffect(() => {
    const bridgeInstance = PeripheralBridge.getBridge()

    if (!bridgeInstance) {
      setViewState('error')
      return
    }

    if (viewState === 'loading') {
      const bridgeHandler = paymentType === 'globalpay' ? 'voidTransaction' : 'handleResult'

      bridgeInstance.registerHandler(bridgeHandler, function(data) {
        const acceptableReasonCodes = ['247', '100']
        const isAccepted = cardReaderApproved(data)
        const isVoid = data.transType === 'void'
        const isAcceptableCode = acceptableReasonCodes.includes(data.reasonCode)

        setViewState('success')
        onSuccess()

        /**
         * TODO (westinschepper): What should we do if the void fails?
         * Right now we always assume the transaction will not process.
         */
        // if ((isAccepted && isVoid) || isAcceptableCode) {
        //   setViewState('success')
        // } else {
        //   setViewState('error')
        // }
      })

      bridgeInstance.registerHandler('errorTransaction', function() {
        setViewState('error')
      })

      let payload = {}

      if (paymentType === 'globalpay') {
        const deviceSerial = getDeviceSerial()

        payload = {
          token,
          orderNumber,
          uuid: orderId,
          deviceSerial,
        }
      } else {
        payload = { requestId, transId }
      }

      bridgeInstance.call('voidTransactionAttempt', payload)
    }

  }, [viewState])

  if (viewState === 'loading') {
    return <p className={styles.confirmText}>Okay, we're cancelling your order!</p>
  } else if (viewState === 'success') {
    return (
      <>
        <p className={styles.confirmText}>Your order has been cancelled.</p>
        <Button onClick={onDone} className={styles.completeButton}>Done</Button>
      </>
    )
  } else if (viewState === 'error') {
    return (
      <>
        <p className={styles.confirmText}>We we're unable to cancel the order, please try again.</p>
        <div className={styles.buttonGroup}>
          <Button onClick={onDone} className={styles.button}>Cancel</Button>
          <Button onClick={() => setViewState('loading')} className={styles.completeButton}>Try Again</Button>
        </div>
      </>
    )
  }
}

const SignatureError = ({ orderId, orderNumber, requestId, transId, token, paymentType, grandTotal, subtotal, itemCount, tip, formattedTotal, onCancel, onSuccess, onDone, className }) => {
  const classNames = cn(styles.signatureError, className)
  const [viewState, setViewState] = useState('idle')

  let content = null
  let primaryText = "Unable to process your transaction"
  let secondaryText = "Please try again or use a different card."

  if (viewState === 'cardReader') {
    primaryText = grandTotal
    secondaryText = `Subtotal: ${subtotal} (${itemCount}) + Tip ${tip}`

    content = (
      <CardReader
        uuid={orderId}
        orderNumber={orderNumber}
        total={formattedTotal}
        onCancel={() => setViewState('idle')}
        onSuccess={onSuccess}
        onDone={onDone}
        authorizationType="sale"
        className={styles.cardReader}
      />
    )
  } else if (viewState === 'idle') {
    primaryText = "Unable to process your transaction"
    secondaryText = "Please try again or use a different card."

    content = (
      <>
        <Button onClick={() => setViewState('cardReader')} className={styles.completeButton}>Try Another Card</Button>
        <p className={styles.orDivider}>or</p>
        <Button onClick={() => setViewState('confirmCancel')} className={styles.button}>Cancel Order</Button>
      </>
    )
  } else if (viewState === 'confirmCancel') {
    content = (
      <>
        <p className={styles.confirmText}>Are you sure you want to cancel this order?</p>
        <div className={styles.buttonGroup}>
          <Button onClick={() => setViewState('idle')} className={styles.button}>No</Button>
          <Button onClick={() => setViewState('cancelling')} className={styles.completeButton}>Yes</Button>
        </div>
      </>
    )
  } else if (viewState === 'cancelling') {
    content = <CancelOrder orderId={orderId} orderNumber={orderNumber} paymentType={paymentType} token={token} requestId={requestId} transId={transId} onDone={onDone} onSuccess={onCancel} />
  }

  return (
    <div className={classNames}>
      <GratuityHeader
        className={cn(styles.gratuityHeader, { [styles.hideHeader]: viewState.toLowerCase().includes('cancel') })}
        primaryText={primaryText}
        secondaryText={secondaryText}
      />
      <div className={styles.contentContainer}>
        {content}
      </div>
    </div>
  )
}

const mapStateToProps = (state, ownProps) => {
  const orderId = get(ownProps, 'match.params.orderUuid', state.order.currentOrderId)
  const getOrder = makeGetOrder()
  const order = getOrder(state, orderId)
  const orderNumber = order?.orderNumber ?? 'unknown'
  const totalInCents = get(order, 'amountInCents', 0)
  const tipAmountInCents = get(order, 'tipAmountInCents', 0)
  const formattedTotal = `${(totalInCents / 100).toFixed(2)}`
  const itemCount = sumBy(order.itemModels, 'quantity')
  const requestId = get(order, 'cardReaderData.requestId', '')
  const transId = get(order, 'cardReaderData.transId', '')
  const token = order?.cardReaderData?.tokenInformation?.token
  const paymentType = getPaymentType(state)

  return {
    orderId,
    itemCount,
    paymentType,
    token,
    requestId,
    transId,
    orderNumber,
    formattedTotal,
    grandTotal: `$${formattedTotal}`,
    subtotal: `$${((totalInCents - tipAmountInCents) / 100).toFixed(2)}`,
    tip: `$${(tipAmountInCents / 100).toFixed(2)}`,
  }
}

const mapDispatchToProps = (dispatch, ownProps) => {
  const push = get(ownProps, 'history.push', () => {})
  const queryParams = queryString.parse(get(ownProps, 'location.search', ''))
  const orderUuid = get(ownProps, 'match.params.orderUuid', '')

  return {
    onCancel: () => {
      dispatch(removeOrder(orderUuid))
      dispatch(clearCart())
      dispatch(clearPromotionsOnOrder())
    },
    onDone: () => {
      push('/concession-order')
    },
    onSuccess: (cardReaderData) => {
      dispatch(updateCardReaderData({ orderId: orderUuid, cardReaderData }))
      push(`/receipt/${orderUuid}`)
    },
  }
}

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(SignatureError))
